/**
 * Data layer for `campaign-wizard` (FS-801, API feat #59).
 *
 * Mirrors the four server routes mounted at /api/v1/campaign-wizard:
 *   POST   /draft                         -> createDraft
 *   GET    /draft/:id                     -> getDraft
 *   POST   /draft/:id/step/:step_name     -> saveStep
 *   POST   /draft/:id/finalize            -> finalizeDraft
 *
 * Raw `fetch()` is confined to this file per the dash D6 contract. Pure
 * helpers (validators, query keys) are exported for unit tests so the
 * client can short-circuit obviously bad payloads before hitting the
 * network and keep its contract in sync with the server's validators.
 */

// ---------------------------------------------------------------------------
// Step catalogue — must stay in lock-step with the server's WIZARD_STEPS.
// The UI collapses the 5-step server flow into a 3-step visual wizard; the
// two synthetic steps (`payout`, `caps`) get sensible defaults posted
// in-band before the review/finalize step so the API's full-draft
// validator accepts the finalize request.
// ---------------------------------------------------------------------------

export const WIZARD_STEPS = ["basics", "targeting", "payout", "caps", "review"] as const;
export type WizardStep = (typeof WIZARD_STEPS)[number];

export const UI_STEPS = [1, 2, 3] as const;
export type UiStep = (typeof UI_STEPS)[number];

export interface BasicsPayload {
	name: string;
	offer_id: string;
	description?: string;
}

export interface TargetingPayload {
	countries: string[];
	devices?: string[];
	os?: string[];
	languages?: string[];
}

export const PAYOUT_MODELS = ["cpa", "cpi", "cps", "cpl", "revshare"] as const;
export type PayoutModel = (typeof PAYOUT_MODELS)[number];

export interface PayoutPayload {
	model: PayoutModel;
	amount: number;
	currency: string;
}

export interface CapsPayload {
	daily?: number;
	monthly?: number;
	total?: number;
}

export interface ReviewPayload {
	confirmed: boolean;
}

export interface StepsBlob {
	basics?: BasicsPayload;
	targeting?: TargetingPayload;
	payout?: PayoutPayload;
	caps?: CapsPayload;
	review?: ReviewPayload;
}

// ---------------------------------------------------------------------------
// Row shapes — match the JSON the server emits.
// ---------------------------------------------------------------------------

export type DraftStatus = "draft" | "finalized";

export interface CampaignDraftRow {
	id: string;
	tenant_id: string;
	current_step: WizardStep;
	status: DraftStatus;
	steps: StepsBlob;
	created_at: number;
	updated_at: number;
	finalized_at?: number | null;
	campaign_id?: string | null;
	next_steps?: readonly string[];
}

export interface StepSaveResponse {
	id: string;
	current_step: WizardStep;
	status: DraftStatus;
	steps: StepsBlob;
	updated_at: number;
}

export interface CampaignRow {
	id: string;
	tenant_id: string;
	name: string;
	offer_id: string;
	status: "active" | "paused" | "archived";
	targeting: TargetingPayload | Record<string, unknown>;
	payout: PayoutPayload | Record<string, unknown>;
	caps: CapsPayload | Record<string, unknown>;
	source_draft: string | null;
	created_at: number;
	updated_at: number;
}

export interface FinalizeResponse {
	draft: {
		id: string;
		status: DraftStatus;
		finalized_at: number | null;
		campaign_id: string | null;
	};
	campaign: CampaignRow;
}

// ---------------------------------------------------------------------------
// Validators — exported so CampaignWizardPage can short-circuit invalid
// submissions AND so the unit tests can lock the client contract against
// the server's shape. Each validator returns `null` on invalid input or
// the normalized value on success. Keep in lock-step with the lib
// validator in `apps/api/src/lib/campaign-wizard.ts`.
// ---------------------------------------------------------------------------

export const NAME_MIN = 1;
export const NAME_MAX = 120;
export const OFFER_ID_MIN = 1;
export const OFFER_ID_MAX = 64;
export const DESCRIPTION_MAX = 500;

export function validateName(raw: unknown): string | null {
	if (typeof raw !== "string") return null;
	const trimmed = raw.trim();
	if (trimmed.length < NAME_MIN || trimmed.length > NAME_MAX) return null;
	return trimmed;
}

export function validateOfferId(raw: unknown): string | null {
	if (typeof raw !== "string") return null;
	const trimmed = raw.trim();
	if (trimmed.length < OFFER_ID_MIN || trimmed.length > OFFER_ID_MAX) return null;
	return trimmed;
}

export function validateDescription(raw: unknown): string | null {
	if (raw === undefined || raw === null) return null;
	if (typeof raw !== "string") return null;
	const trimmed = raw.trim();
	if (trimmed.length === 0) return null;
	if (trimmed.length > DESCRIPTION_MAX) return null;
	return trimmed;
}

/**
 * Parse the free-form "countries" textarea into a normalized `string[]`.
 * Accepts comma- and newline-separated ISO-2 codes; returns `null` if the
 * resulting list is empty.
 */
export function validateCountries(raw: unknown): string[] | null {
	if (Array.isArray(raw)) {
		const list = raw
			.filter((v): v is string => typeof v === "string")
			.map((v) => v.trim().toUpperCase())
			.filter((v) => v.length > 0);
		return list.length > 0 ? Array.from(new Set(list)) : null;
	}
	if (typeof raw !== "string") return null;
	const list = raw
		.split(/[\s,]+/)
		.map((v) => v.trim().toUpperCase())
		.filter((v) => v.length > 0);
	return list.length > 0 ? Array.from(new Set(list)) : null;
}

export function validatePayoutModel(raw: unknown): PayoutModel | null {
	return typeof raw === "string" && (PAYOUT_MODELS as readonly string[]).includes(raw)
		? (raw as PayoutModel)
		: null;
}

export function validatePayoutAmount(raw: unknown): number | null {
	const n = typeof raw === "number" ? raw : Number(raw);
	if (!Number.isFinite(n) || n < 0) return null;
	return n;
}

export function validateCurrency(raw: unknown): string | null {
	if (typeof raw !== "string") return null;
	const up = raw.trim().toUpperCase();
	if (up.length !== 3) return null;
	if (!/^[A-Z]{3}$/.test(up)) return null;
	return up;
}

// ---------------------------------------------------------------------------
// Defaults applied to the two synthetic steps so the 3-step UI can finalize
// a draft the 5-step API considers complete.
// ---------------------------------------------------------------------------

export const DEFAULT_PAYOUT: PayoutPayload = {
	model: "cpa",
	amount: 0,
	currency: "USD",
};

export const DEFAULT_CAPS: CapsPayload = {};

// ---------------------------------------------------------------------------
// Query-key factory — stable, serializable keys for TanStack invalidation.
// ---------------------------------------------------------------------------

export const campaignWizardQueryKeys = {
	all: ["campaign-wizard"] as const,
	list: () => [...campaignWizardQueryKeys.all, "list"] as const,
	detail: (id: string) => [...campaignWizardQueryKeys.all, "detail", id] as const,
};

// ---------------------------------------------------------------------------
// Remote bindings — raw `fetch()` is intentional and confined here.
// ---------------------------------------------------------------------------

const BASE = "/api/v1/campaign-wizard";
const TENANT_ID =
	(typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, unknown> }).env
		? ((import.meta as { env: Record<string, unknown> }).env.VITE_TENANT_ID as string | undefined)
		: undefined) ?? "demo";

function headers(extra?: Record<string, string>): Record<string, string> {
	return {
		accept: "application/json",
		"x-tenant-id": TENANT_ID,
		...(extra ?? {}),
	};
}

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function createDraft(): Promise<CampaignDraftRow> {
	const res = await fetch(`${BASE}/draft`, {
		method: "POST",
		credentials: "include",
		headers: headers({ "content-type": "application/json" }),
		body: JSON.stringify({}),
	});
	return parseOrThrow<CampaignDraftRow>(res);
}

export async function getDraft(id: string): Promise<CampaignDraftRow> {
	const res = await fetch(`${BASE}/draft/${encodeURIComponent(id)}`, {
		method: "GET",
		credentials: "include",
		headers: headers(),
	});
	return parseOrThrow<CampaignDraftRow>(res);
}

export async function saveStep(args: {
	id: string;
	step: WizardStep;
	payload: unknown;
}): Promise<StepSaveResponse> {
	const res = await fetch(
		`${BASE}/draft/${encodeURIComponent(args.id)}/step/${encodeURIComponent(args.step)}`,
		{
			method: "POST",
			credentials: "include",
			headers: headers({ "content-type": "application/json" }),
			body: JSON.stringify(args.payload ?? {}),
		},
	);
	return parseOrThrow<StepSaveResponse>(res);
}

export async function finalizeDraft(id: string): Promise<FinalizeResponse> {
	const res = await fetch(
		`${BASE}/draft/${encodeURIComponent(id)}/finalize`,
		{
			method: "POST",
			credentials: "include",
			headers: headers({ "content-type": "application/json" }),
			body: JSON.stringify({}),
		},
	);
	return parseOrThrow<FinalizeResponse>(res);
}

// ---------------------------------------------------------------------------
// Compatibility surface — Wizard.tsx was authored against an earlier shape.
// Re-export the same entities under the names/shapes it imports. Do NOT
// remove; removing breaks Wizard.tsx type-checking and the dash home render.
// ---------------------------------------------------------------------------

export type CampaignDraftResponse = CampaignDraftRow;

export interface TemplateListItem {
	id: string;
	template_code: string;
	name: string;
	description?: string | null;
	vertical?: string | null;
	is_active?: boolean;
	scope?: "platform" | "tenant";
}

export async function listTemplates(): Promise<TemplateListItem[]> {
	const res = await fetch("/api/v1/campaign-templates/list", {
		method: "GET",
		credentials: "include",
		headers: headers(),
	});
	const rows = await parseOrThrow<
		Array<{
			id: string;
			template_code: string;
			name: string;
			description?: string | null;
			vertical?: string | null;
			is_active?: boolean;
			scope?: "platform" | "tenant";
		}>
	>(res);
	return rows.map((r) => ({
		id: r.id,
		template_code: r.template_code,
		name: r.name,
		description: r.description ?? null,
		vertical: r.vertical ?? null,
		is_active: r.is_active,
		scope: r.scope,
	}));
}

export function parseCountryList(raw: string): string[] {
	if (typeof raw !== "string") return [];
	const list = raw
		.split(/[\s,]+/)
		.map((v) => v.trim().toUpperCase())
		.filter((v) => v.length > 0);
	return Array.from(new Set(list));
}

export const startDraft = createDraft;

export function saveDraftStep(
	id: string,
	step: WizardStep,
	payload: unknown,
): Promise<CampaignDraftRow> {
	// The earlier Wizard contract expected the full draft row back after a
	// step save. The current server returns only the incremental fields, so
	// we rehydrate the draft via getDraft to keep Wizard's setDraft() path
	// working end-to-end.
	return saveStep({ id, step, payload }).then(() => getDraft(id));
}

// ---------------------------------------------------------------------------
// Compatibility validators — shape { ok: boolean, fields: Record<string,string> }
// ---------------------------------------------------------------------------

export type ValidationResult<T> =
	| { ok: true; value: T; fields: Record<string, string> }
	| { ok: false; fields: Record<string, string> };

export type StepValidation<T> = ValidationResult<T>;

export function validateBasics(raw: BasicsPayload): ValidationResult<BasicsPayload> {
	const fields: Record<string, string> = {};
	const name = validateName(raw?.name);
	if (name === null) fields.name = "Name is required (1–120 characters).";
	const offer_id = validateOfferId(raw?.offer_id);
	if (offer_id === null) fields.offer_id = "Pick a template.";
	let description: string | undefined;
	if (raw?.description !== undefined && raw.description !== "") {
		const d = validateDescription(raw.description);
		if (d === null) fields.description = "Keep description under 500 characters.";
		else description = d;
	}
	if (Object.keys(fields).length > 0) return { ok: false, fields };
	return {
		ok: true,
		value: {
			name: name as string,
			offer_id: offer_id as string,
			...(description !== undefined ? { description } : {}),
		},
		fields: {},
	};
}

export function validateTargeting(
	raw: TargetingPayload,
): ValidationResult<TargetingPayload> {
	const fields: Record<string, string> = {};
	const countries = validateCountries(raw?.countries);
	if (countries === null) fields.countries = "Add at least one ISO-2 country code.";
	if (Object.keys(fields).length > 0) return { ok: false, fields };
	return {
		ok: true,
		value: { ...raw, countries: countries as string[] },
		fields: {},
	};
}

export function validateReview(
	raw: ReviewPayload,
): ValidationResult<ReviewPayload> {
	const fields: Record<string, string> = {};
	if (!raw?.confirmed) fields.confirmed = "Please confirm the details.";
	if (Object.keys(fields).length > 0) return { ok: false, fields };
	return { ok: true, value: { confirmed: true }, fields: {} };
}
